import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  HostListener,
  Inject,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import {
  atxWidth$,
  ATX_BREAKPOINT_WIDTH,
} from '@epicuro-next/ataraxia/breakpoint';
import { IMAGE_MAX_SIZES } from '@epicuro-next/ataraxia/image';
import { PopoverDirective } from '@epicuro-next/ataraxia/popover';
import {
  availableInventoryTooltipLabel,
  trueDemandInventoryTooltipLabel,
  variantInOpenedListLocale,
} from '@epicuro-next/constants/features/localization';
import { CellData } from '@epicuro-next/features/config-strip/utilities/materials';
import {
  configStripConfigurationDefaultValues,
  ConfigStripConfigurationKeys,
  ConfigStripConfigurations,
  CONFIG_STRIP_FEATURE,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import {
  maxVisibleInventoriesRule,
  selectMaxUnlimitedQuantityOrder,
} from '@epicuro-next/state/config-strip-state';
import { ProductListActionsBaseComponentStore } from '@epicuro-next/state/product-list-state';
import { isNonNullish, isNullish, Placement } from '@epicuro-next/utilities';
import { getZoom } from '@epicuro-next/utilities-browser';
import { Store } from '@ngrx/store';
import { combineLatest, fromEvent, Observable, Subscription } from 'rxjs';
import {
  filter,
  map,
  sampleTime,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {
  getCellInventories,
  getCellInventoryTotals,
} from '../../../shared/cell/cell-inventory';
import { ConfigStripUiStore } from '../../../store/config-strip-ui.store';
import { QuantityChangedPayload } from '../../../types';
import { NeverOutOfStockIconSize } from '../../never-out-of-stock-icon/interfaces/never-out-of-stock-icon-size.enum';
import { BaseSizeGridComponent } from '../base-size-grid.component';

import { getVisibleInventoriesLimit } from './get-visible-inventories-limit';
import { fixColumnAndRowGap } from './size-grid-borders';

const BREAKPOINT = `(max-width: ${ATX_BREAKPOINT_WIDTH.MD}px)`;

interface ICellCoord {
  x: number | null;
  y: number | null;
  subY: number | null; // need to track the secondary dimension row somehow
}

interface ICoords {
  start: ICellCoord;
  end: ICellCoord;
}

@Component({
  selector: 'epc-size-grid',
  templateUrl: './size-grid.component.html',
  styleUrls: ['./size-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SizeGridComponent
  extends BaseSizeGridComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  public csNameSpace = CONFIG_STRIP_FEATURE;
  public configStripConfigurationKeys = ConfigStripConfigurationKeys;
  public imageMaxSizes = IMAGE_MAX_SIZES;
  public variantInOpenedListLocale = variantInOpenedListLocale;

  public readonly singleSizeRunIcon$ = this.store.select(
    selectConfigurationValueFrom<
      ConfigStripConfigurationKeys.SingleSizeCopyIcon,
      ConfigStripConfigurations
    >(CONFIG_STRIP_FEATURE, ConfigStripConfigurationKeys.SingleSizeCopyIcon, [
      'fas',
      'angles-right',
    ]),
  );

  public readonly multipleSizeRunIcon$ = this.store.select(
    selectConfigurationValueFrom<
      ConfigStripConfigurationKeys.MultipleSizeCopyIcon,
      ConfigStripConfigurations
    >(CONFIG_STRIP_FEATURE, ConfigStripConfigurationKeys.MultipleSizeCopyIcon, [
      'fas',
      'angles-right',
    ]),
  );
  public readonly canPasteFromBuffer$ = this.store.select(
    selectConfigurationValueFrom<
      ConfigStripConfigurationKeys.PasteFromBuffer,
      ConfigStripConfigurations
    >(
      CONFIG_STRIP_FEATURE,
      ConfigStripConfigurationKeys.PasteFromBuffer,
      configStripConfigurationDefaultValues[
        ConfigStripConfigurationKeys.PasteFromBuffer
      ],
    ),
  );

  public readonly canCopyFromGrid$ = this.store.select(
    selectConfigurationValueFrom<
      ConfigStripConfigurationKeys.CopyFromGrid,
      ConfigStripConfigurations
    >(
      CONFIG_STRIP_FEATURE,
      ConfigStripConfigurationKeys.CopyFromGrid,
      configStripConfigurationDefaultValues[
        ConfigStripConfigurationKeys.CopyFromGrid
      ],
    ),
  );

  public readonly MAX_CUSTOMER_PRODUCT_IDENTIFIER_LENGTH = 35;

  public NeverOutOfStockIconSize = NeverOutOfStockIconSize;

  public customerProductIdentifierFormControl = new UntypedFormControl(
    '',
    Validators.maxLength(this.MAX_CUSTOMER_PRODUCT_IDENTIFIER_LENGTH),
  );

  public readonly availableInventoryTooltipLabel =
    availableInventoryTooltipLabel;
  public readonly trueDemandInventoryTooltipLabel =
    trueDemandInventoryTooltipLabel;
  public readonly tooltipPlacement = Placement;

  public copyCoords: ICoords = {
    start: { x: null, y: null, subY: null },
    end: { x: null, y: null, subY: null },
  };

  public displayCopyCoords: ICoords = {
    start: { x: null, y: null, subY: null },
    end: { x: null, y: null, subY: null },
  };

  private basketData$$: Subscription;

  private readonly pageStore = inject(ConfigStripUiStore);

  @ViewChild('customerProductIdentifierInput')
  public customerProductIdentifierInput: ElementRef<HTMLInputElement>;

  @Input()
  public disableOutsideClick = false;

  @Input()
  public showShowMoreInventories = true;

  @Input()
  public showGridInbound = false;

  @Input()
  public portable = false;

  @Input()
  public set basketData$(basketData$: Observable<void>) {
    if (this.basketData$$) this.basketData$$.unsubscribe();
    this.basketData$$ = basketData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.sizeRunRefreshPending$$.next(true));
  }

  @Input()
  public focusOnFirstInput = false;

  /** used to identify which row to highlight */
  public highlightRow?: number;

  /** identifies the currently selected cell */
  public activeCell: { materialId?: string; row?: number; column?: number };
  /** currently selected scale */
  public activeScale: { [key: string]: string[] };

  protected sizeRunRefreshPending = true;

  public visibleInventoriesLimit$ = combineLatest([
    atxWidth$,
    this.store.select(maxVisibleInventoriesRule),
  ]).pipe(
    map(([screenWidth, maxInventories]) =>
      getVisibleInventoriesLimit(screenWidth, maxInventories),
    ),
  );

  public productTotals = computed(() => {
    return this.colTotals()
      .filter(isNonNullish)
      .reduce((acc, item) => acc + item, 0);
  });

  public trueDemandTotals = computed(() => {
    return this.trueDemandColTotals()
      .filter(isNonNullish)
      .reduce((acc, item) => acc + item, 0);
  });

  /** returns the columns configuration for the grid. */
  public get gridColumnsStyle() {
    const totalColumnWidth = this.scaleSelectorVisible
      ? '5rem'
      : this.gridColumnWidth;

    const headerColumnWidth = this.portable ? 'minmax(18rem, 20rem)' : 'auto';

    /** color column + the only size column (needed for "one size") */
    if (this.xSizes.length === 1) {
      return `${headerColumnWidth} 5rem ${
        this.showTotals ? totalColumnWidth : ''
      }`;
    }

    //  color column + size columns + totals
    return `${headerColumnWidth} repeat(${this.xSizes.length}, ${
      this.gridColumnWidth
    }) ${this.showTotals ? totalColumnWidth : ''}`;
  }

  public get subGridRowsStyle() {
    return `minmax(${this.gridColumnMinHeight}, 1fr)`;
  }

  /** returns the columns configuration for the grid containing the sizes. */
  public get subGridColumnsStyle() {
    return `repeat(${this.xSizes.length}, ${this.gridColumnWidth})`;
  }

  /** returns the column end for the sub grid. */
  public get subGridColumnEnd() {
    return `span  ${this.xSizes.length}`;
  }

  /* returns the sum width for an unavailable message */
  public get subGridUnavailableMsgSize() {
    const length = this.xSizes.length >= 8 ? this.xSizes.length : 8;
    return (
      length * Number.parseFloat(this.gridColumnWidth) +
      Number.parseFloat(this.gridColumnWidth) +
      `rem`
    );
  }

  private get gridColumnWidth() {
    const isSmallDevice = this.breakpointObserver.isMatched(BREAKPOINT);

    /** width for the only size column (needed for "one size") */
    if (this.xSizes.length === 1) {
      return '5rem';
    }

    return isSmallDevice ? '2.5rem' : '3.75rem';
  }

  private get gridColumnMinHeight() {
    const isSmallDevice = this.breakpointObserver.isMatched(BREAKPOINT);
    return isSmallDevice ? '2.5' : '3rem';
  }

  /** Array of sizes to show as row headers */
  public ySizes: string[] = [];
  /** Array of sizes to show as column headers */
  public xSizes: string[] = [];

  /** current hovered row identifier composed by the material ID + row number */
  private currentHoveredRow = '';

  private isShiftKeyCurrentlyPressed = false;

  public getCellInventoryTotals = getCellInventoryTotals;
  public getCellInventories = getCellInventories;

  constructor(
    fb: UntypedFormBuilder,
    cdr: ChangeDetectorRef,
    componentStore: ProductListActionsBaseComponentStore,
    public element: ElementRef,
    private breakpointObserver: BreakpointObserver,
    @Inject(DOCUMENT) private document: Document,
    private store: Store,
  ) {
    super(fb, cdr, componentStore);

    this.deliveryItemsVoid$$
      .pipe(
        switchMap(() => this.sizeRunRefreshPending$$),
        filter(Boolean),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        super.refreshData();
        if (this.focusOnFirstInput) {
          this.focusFirstInput();
        }
        this.sizeRunRefreshPending$$.next(false);
      });
  }

  @HostListener('window:keydown.shift', ['$event'])
  @HostListener('window:keyup.shift', ['$event'])
  public onShiftKeyEvents(event: KeyboardEvent) {
    this.isShiftKeyCurrentlyPressed = event.shiftKey;
  }

  @HostListener('window:keydown.control.c', ['$event'])
  @HostListener('window:keydown.meta.c', ['$event'])
  public onCtrlC(event: KeyboardEvent) {
    this.copy(event);
  }

  public ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (changes.deliveryItems?.firstChange && this.focusOnFirstInput) {
      // check: figure out how to do this without setTimeout
      setTimeout(() => this.focusFirstInput());
    }
    if (
      // we exclude condition that is already checked in base class
      !(
        changes.materials ||
        changes.sizesInventories ||
        changes.deliveryShop
      ) &&
      changes.deliveryItems
    ) {
      this.deliveryItemsVoid$$.next();
    }
  }

  public onCustomerProductIdentifierPopoverOpen(
    popoverInstance: PopoverDirective,
    productId: string,
  ) {
    this.setCustomerProductIdentifierFormControlValue(
      this.getCustomerProductIdentifier(productId),
    );

    // the input is only set on the next turn
    requestAnimationFrame(() => {
      this.customerProductIdentifierInput.nativeElement.select();

      fromEvent(this.customerProductIdentifierInput.nativeElement, 'keyup')
        .pipe(
          filter((event: KeyboardEvent) => event.key === 'Enter'),
          takeUntil(popoverInstance.atxPopoverClose),
        )
        .subscribe(() => {
          popoverInstance.close();
        });
    });
  }

  public ngAfterViewInit() {
    const fixColumnAndRowGapCalculatedDefault = fixColumnAndRowGap(
      this.document,
    );
    const baseZoom = getZoom(this.document.defaultView);
    if (isNonNullish(baseZoom) && baseZoom !== 100) {
      fixColumnAndRowGapCalculatedDefault(baseZoom);
    }

    fromEvent(window, 'resize')
      .pipe(
        takeUntil(this.destroy$),
        sampleTime(1500),
        map(() => getZoom(this.document.defaultView)),
        filter(isNonNullish),
        tap((zoom: number) => fixColumnAndRowGapCalculatedDefault(zoom)),
      )
      .subscribe();
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
  }

  public setCustomerProductIdentifierFormControlValue(value: string) {
    this.customerProductIdentifierFormControl.patchValue(value, {
      emitEvent: false,
    });
  }

  public updateCustomerProductIdentifier(productId: string, brandId: string) {
    const customerProductIdentifier =
      this.customerProductIdentifierFormControl.value;

    if (
      this.customerProductIdentifierFormControl.valid &&
      customerProductIdentifier !== this.getCustomerProductIdentifier(productId)
    ) {
      this.customerProductIdentifierUpdate.emit({
        productId,
        brandId,
        customerProductIdentifier,
      });
    }
  }

  /**
   * Returns true if the row is the last clicked row
   * @param materialId
   * @param row
   */
  public isRowActive(materialId: string, row: number) {
    if (this.preSelectedMaterial === materialId) {
      return true;
    }

    return (
      this.activeCell &&
      this.activeCell.materialId === materialId &&
      this.activeCell.row === row
    );
  }

  /**
   * Returns true if the row is deletable
   * @param materialId
   * @param row
   */
  public isRowDeletable(materialId: string, row: number) {
    const rowId = `${materialId}-${row}`;
    if (this.currentHoveredRow !== rowId) {
      return false;
    }
    return (this.rowTotals()[materialId][row] ?? 0) > 0;
  }

  /**
   * Clear quantities for a given size row and emit the values
   * @param materialId
   * @param row
   */
  public clearRowQuantities(materialId: string, row: number) {
    const targetData = (this.getMaterialData(materialId) ?? [])[row];
    if (!targetData || targetData?.[row]?.isLocked) {
      return;
    }

    const newQuantities: QuantityChangedPayload[] = targetData.map((data) => {
      return {
        productId: data.productId,
        quantity: 0,
      };
    });

    this.quantityChange.emit(newQuantities);
    this.clearActiveGridCell();
    this.clearSelection();
    this.refreshData();
  }

  /**
   * Returns true if the column is the last clicked column
   * @param column
   */
  public isColumnActive(column: number) {
    return this.activeCell?.column === column;
  }

  public hoverRow(materialId: string, row: number, value = true) {
    this.currentHoveredRow = value ? `${materialId}-${row}` : '';
  }

  public clearActiveGridCell() {
    const elm = this.element.nativeElement.querySelector(
      'input',
    ) as HTMLDivElement;
    elm?.blur();
    this.activeCell = {};
    this.activeRow = null;
  }

  public clearSelection() {
    this.sizeSelect.emit({ productId: undefined, materialId: undefined });
  }

  public clearCopyActiveRow() {
    this.copyActiveRow = null;
  }

  public showSizeRun(materialId: string, row: number) {
    if (
      !this.readonly &&
      Boolean(this.getMaterialData(materialId)?.[0]?.[0]?.isLocked) === false &&
      this.copyActiveRow &&
      (this.copyActiveRow.material !== materialId ||
        this.copyActiveRow.row !== row) &&
      !this.disabledMaterialsIds?.[materialId]
    ) {
      const rowTotal =
        this.rowTotals()[this.copyActiveRow.material]?.[this.copyActiveRow.row];
      if (rowTotal > 0) return true;
    }
    return false;
  }

  public getSizeRunIcon(materialId: string, elementRow: number) {
    return this.isSingleSizeRun(materialId, elementRow)
      ? this.singleSizeRunIcon$
      : this.multipleSizeRunIcon$;
  }

  protected isSingleSizeRun(materialId: string, elementRow: number) {
    if (!this.copyActiveRow) {
      return false;
    }
    const { row, material } = this.copyActiveRow;

    const sourceMaterialRows = this.getMaterialData(material)?.length ?? 0;
    if (materialId === material && row + 1 === elementRow) {
      return true;
    } else {
      const distanceBetweenMaterials = Math.abs(
        (materialId !== material &&
          this.getMaterialIndex(materialId) -
            this.getMaterialIndex(material)) ||
          0,
      );
      if (
        distanceBetweenMaterials < 1 ||
        (distanceBetweenMaterials === 1 &&
          elementRow === 0 &&
          row === sourceMaterialRows - 1)
      ) {
        return true;
      }
    }

    return this.isShiftKeyCurrentlyPressed === false;
  }

  public applySizeRun(targetMaterialId: string, targetRow: number) {
    if (!this.copyActiveRow) {
      return;
    }

    const sourceData = (this.getMaterialData(this.copyActiveRow.material) ??
      [])[this.copyActiveRow.row];
    if (!sourceData) {
      return;
    }

    if (this.isSingleSizeRun(targetMaterialId, targetRow)) {
      const data = this.prepareSingleSizeRunChanges(
        targetMaterialId,
        targetRow,
        sourceData,
      );

      if (data.length) {
        this.quantityChange.emit(data as QuantityChangedPayload[]);
      }
    } else {
      this.applyMultipleSizeRun(targetMaterialId, targetRow, sourceData);
    }
    this.sizeRunRefreshPending$$.next(true);
  }

  protected prepareSingleSizeRunChanges(
    targetMaterialId: string,
    targetRow: number,
    sourceData: CellData[],
  ) {
    const targetData = (this.getMaterialData(targetMaterialId) ?? [])[
      targetRow
    ];
    if (!targetData || targetData?.[0]?.isLocked) {
      return [];
    }

    return sourceData
      .map((cellData, index) => {
        const originalData = targetData[index];
        if (!originalData) {
          return;
        }
        const quantityToApply = (
          this.deliveryItems.items[cellData.productId] ?? {}
        ).quantity;

        return {
          productId: originalData.productId,
          quantity: quantityToApply,
        };
      })
      .filter(Boolean);
  }

  protected applyMultipleSizeRun(
    targetMaterialId: string,
    targetRow: number,
    sourceData: CellData[],
  ) {
    const copyActiveRow = this.copyActiveRow;
    if (!copyActiveRow) {
      return;
    }

    let targetsMaterials = [targetMaterialId];
    if (targetMaterialId !== copyActiveRow.material) {
      const copyFrom = this.getMaterialIndex(copyActiveRow.material);
      const copyTo = this.getMaterialIndex(targetMaterialId);

      if (copyFrom < copyTo) {
        targetsMaterials = this.materials
          .slice(copyFrom, copyTo + 1)
          .map(({ productId }) => productId);
      } else {
        targetsMaterials = this.materials
          .slice(copyTo, copyFrom)
          .map(({ productId }) => productId);
      }
    }

    const changes: QuantityChangedPayload[] = [];
    targetsMaterials.forEach((materialId) => {
      const untilRow =
        materialId === targetMaterialId
          ? targetRow
          : (this.getMaterialData(materialId)?.length || 1) - 1;
      for (let index = 0; index <= untilRow; index++) {
        if (
          materialId === copyActiveRow.material &&
          index <= copyActiveRow.row
        ) {
          continue;
        }
        const sizeRunChanges = this.prepareSingleSizeRunChanges(
          materialId,
          index,
          sourceData,
        );
        changes.push(...(sizeRunChanges as QuantityChangedPayload[]));
      }
    });

    this.quantityChange.emit(changes);
  }

  protected getMaterialIndex(materialId: string) {
    return this.materials.findIndex(
      ({ productId }) => materialId === productId,
    );
  }

  protected isActiveCell(productId: string, row: number, column: number) {
    return (
      this.activeCell &&
      this.activeCell.materialId === productId &&
      this.activeCell.row === row &&
      this.activeCell.column === column
    );
  }

  public hasRowQuantities(rowTotal?: number[]) {
    return (rowTotal ?? []).some((total) => total > 0);
  }

  // tslint:disable-next-line:cognitive-complexity
  public onClipboardPaste(
    payload: string[][],
    column: number,
    materialId: string,
    secondaryIndex: number,
  ) {
    combineLatest([
      this.canPasteFromBuffer$,
      this.store.select(selectMaxUnlimitedQuantityOrder),
    ])
      .pipe(
        take(1),
        filter(([canPaste]) => !!canPaste),
      )
      .subscribe(([_, maxUnlimitedQuantityOrder]) => {
        const changes: QuantityChangedPayload[] = [];
        const startMaterialIndex = this.getMaterialIndex(materialId);
        const rows = this.materials
          .slice(startMaterialIndex)
          .flatMap(({ productId }, rowIndex) => {
            const subRows = this.materialsMatrix.get(productId) ?? [];
            return rowIndex === 0
              ? // if this is a first row, cut the unneeded sub-rows
                // slice comes first bc secondaryIndex may be referring to a row hidden with getRowInventorySum method in HTML
                subRows
                  .slice(secondaryIndex)
                  .filter(
                    (__, index) =>
                      !!this.getRowInventorySum(
                        productId,
                        index + secondaryIndex,
                      ),
                  )
              : subRows.filter(
                  (__, index) => !!this.getRowInventorySum(productId, index),
                );
          });

        payload.forEach((rowQuantities, index) => {
          const row = rows[index];
          // check if row is in range
          if (!row) return;
          rowQuantities.forEach((col, colIndex) => {
            const size = row[column + colIndex];
            // check if column is in range
            if (size) {
              // consider text is pasted
              const newQuantity = isNaN(+col) ? 0 : +col;
              const inBasketQuantity = size.quantity ?? 0;
              // handled max limited value rule, note then basket item can have quantity more than rule
              const quantityToApply = Math.min(
                newQuantity,
                Math.max(inBasketQuantity, maxUnlimitedQuantityOrder as number),
              );
              changes.push({
                productId: size.productId,
                quantity: isNaN(quantityToApply)
                  ? newQuantity
                  : quantityToApply,
              });
            }
          });
        });
        if (changes.length) {
          this.quantityChange.emit(changes);
          this.refreshData();
        }
      });
  }

  public focusFirstInput() {
    if (!this.element) return;

    const elm = this.element.nativeElement.querySelector(
      'input',
    ) as HTMLDivElement;
    if (elm) {
      elm.click();
      elm.focus();
    }
  }

  public setCopyStartCoords(
    x: number | null,
    y: number | null,
    subY: number | null,
  ) {
    this.copyCoords = {
      ...this.copyCoords,
      start: { x, y, subY },
    };
  }

  public setCopyEndCoords(
    x: number | null,
    y: number | null,
    subY: number | null,
  ) {
    this.copyCoords = {
      ...this.copyCoords,
      end: { x, y, subY },
    };

    this.updateDisplayCopyCoords();
  }

  public updateDisplayCopyCoords() {
    const { start, end } = this.copyCoords;
    const startX = start.x;
    const endX = end.x;
    const startY = start.y;
    const endY = end.y;
    const endSubY = end.subY;
    const startSubY = start.subY;

    if (
      isNullish(startX) ||
      isNullish(endX) ||
      isNullish(startY) ||
      isNullish(endY) ||
      isNullish(startSubY) ||
      isNullish(endSubY)
    )
      return;

    this.displayCopyCoords = { start: { ...start }, end: { ...end } };

    if (startX > endX) {
      this.displayCopyCoords.start.x = endX;
      this.displayCopyCoords.end.x = startX;
    }

    if (startY > endY) {
      this.displayCopyCoords.start.y = endY;
      this.displayCopyCoords.end.y = startY;
      this.displayCopyCoords.start.subY = endSubY;
      this.displayCopyCoords.end.subY = startSubY;
    }
    if (startY === endY && startSubY > endSubY) {
      this.displayCopyCoords.start.subY = endSubY;
      this.displayCopyCoords.end.subY = startSubY;
    }
  }

  public onClick(event: MouseEvent) {
    const input = (event.target as HTMLElement).offsetParent?.querySelector(
      'input',
    );
    if (input) {
      input.focus();
    }
    this.resetCopyCoords();
  }

  private mapCoords(coords: ICoords) {
    const startX = coords.start.x;
    const startY = coords.start.y;
    const startSubY = coords.start.subY;
    // coordinates of end cell (opposite square corner)
    const endX = coords.end.x;
    const endY = coords.end.y;
    const endSubY = coords.end.subY;

    return { startX, startY, startSubY, endX, endY, endSubY };
  }

  private checkCoords(coords: ICoords): boolean {
    const { startX, startY, startSubY, endX, endY, endSubY } =
      this.mapCoords(coords);

    return !(
      isNullish(startX) ||
      isNullish(startY) ||
      isNullish(endX) ||
      isNullish(endY) ||
      isNullish(startSubY) ||
      isNullish(endSubY)
    );
  }

  // tslint:disable-next-line:cognitive-complexity
  public isInCoords(x: number, y: number, subY: number) {
    if (!this.checkCoords(this.displayCopyCoords)) return;

    const { startX, startY, startSubY, endX, endY, endSubY } = this.mapCoords(
      this.displayCopyCoords,
    ) as { [key: string]: number };

    // this function is run by every cell and checks whenever cell is inside start and end coordinates
    // this check uses rowIndex as y and colIndex as x
    // first it checks if y is inside coords, and then checks x for both ways

    // if user selects cells in scope of one material
    if (startY === endY && x >= startX && x <= endX) {
      return (
        y === startY &&
        (startSubY < endSubY
          ? subY >= startSubY && subY <= endSubY
          : subY <= startSubY && subY >= endSubY)
      );
    }

    // if user selects cells from top to bottom
    if (x >= startX && x <= endX) {
      if (y === startY) {
        return subY >= startSubY;
      } else if (y === endY) {
        return subY <= endSubY;
      } else if (y < endY && y > startY) {
        // if the material is between start and end material, we should select every "sub-row"
        return true;
      }
    }
  }

  // tslint:disable-next-line:cognitive-complexity
  public copy(event: KeyboardEvent) {
    this.canCopyFromGrid$.pipe(take(1)).subscribe((canCopy) => {
      if (!canCopy || !this.checkCoords(this.displayCopyCoords)) return;

      const { startX, startY, startSubY, endX, endY, endSubY } = this.mapCoords(
        this.displayCopyCoords,
      ) as { [key: string]: number };

      event.preventDefault();

      const parse = (i: number) => {
        const row = this.materials[i];
        let subRows;

        // if the row is not first or last, we should select every available subRow
        if (![startX, startY].includes(i)) {
          subRows = this.formGroup
            .get(row.productId)
            ?.value.filter(
              (_: number, index: number) =>
                !!this.getRowInventorySum(row.productId, index),
            )
            .map((subRow: { quantity: number }[]) =>
              subRow.slice(startX, endX + 1),
            );
        }

        // for the first line
        if (i === startY) {
          subRows = this.formGroup
            .get(row.productId)
            ?.value.filter(
              (_: number, index: number) =>
                index >= startSubY &&
                !!this.getRowInventorySum(row.productId, index),
            )
            .map((subRow: { quantity: number }[]) =>
              subRow.slice(startX, endX + 1),
            );
        }
        // for the last line
        if (i === endY) {
          subRows = this.formGroup
            .get(row.productId)
            ?.value.filter(
              (_: number, index: number) =>
                index <= endSubY &&
                !!this.getRowInventorySum(row.productId, index),
            )
            .map((subRow: { quantity: number }[]) =>
              subRow.slice(startX, endX + 1),
            );
        }

        // if user selected only sub-dimensions of one variant
        if (i === endY && i === startY) {
          subRows = this.formGroup
            .get(row.productId)
            ?.value.filter(
              (_: number, index: number) =>
                index <= endSubY &&
                index >= startSubY &&
                !!this.getRowInventorySum(row.productId, index),
            )
            .map((subRow: { quantity: number }[]) =>
              subRow.slice(startX, endX + 1),
            );
        }

        return subRows;
      };
      const result = [];
      for (let i = startY; i <= endY; i++) {
        result.push(...parse(i));
      }
      const toBuffer = result.reduce(
        (acc, curr) =>
          acc +
          `${curr
            .map(({ quantity }: { quantity: number }) => quantity)
            .join('\t')}\n`,
        '',
      );

      navigator.clipboard.writeText(toBuffer);
    });
  }

  public onSizeRunsQuantityChange(materialId: string, quantity: number) {
    if (quantity === 0) {
      this.clearRowQuantities(materialId, 0);
      return;
    }

    const targetData = (this.getMaterialData(materialId) ?? [])[0];
    if (!targetData || targetData?.[0]?.isLocked) {
      return;
    }

    const newQuantities: QuantityChangedPayload[] = targetData.map(
      (data, index) => {
        return {
          productId: data.productId,
          quantity: (this.sizeRunsSizes[index] ?? 0) * quantity,
        };
      },
    );

    this.quantityChange.emit(newQuantities);
    this.refreshData();
  }

  private resetCopyCoords() {
    const defaultValue = {
      start: { x: null, y: null, subY: null },
      end: { x: null, y: null, subY: null },
    };
    this.copyCoords = defaultValue;
    this.displayCopyCoords = defaultValue;
  }

  public openInboundDetails(): void {
    this.pageStore.setInboundDetailsVisibility(true);
  }
}
